import { AccountApprovalStatus } from './account-status'
import type {
  ApplicationEntity,
  ApplicationItem,
  ApplicationSectionIndex,
} from './applications'
import { TagWithAssociations } from './tags'
import { BasicOpsWorkerDetails } from './workers'

export type RankWeights = Record<ScoreCriterionKey, number>

export enum WorkerApplicationStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SYSTEM_PROCESSING = 'SYSTEM_PROCESSING',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  COMPLETED_ELIGIBLE = 'COMPLETED_ELIGIBLE',
  COMPLETED_INELIGIBLE = 'COMPLETED_INELIGIBLE',
}

export enum WorkerApplicationItemStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SYSTEM_PROCESSING = 'SYSTEM_PROCESSING',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  PENDING_PREREQUISITES = 'PENDING_PREREQUISITES',
}

export enum ApplicationActionSource {
  INTERNAL_USER = 'INTERNAL_USER',
  WORKER = 'WORKER',
  GHOST_WORKER = 'GHOST_WORKER',
  SYSTEM = 'SYSTEM',
  EXTERNAL_VENDOR = 'EXTERNAL_VENDOR',
  VETTING_CAMPAIGN_SYNC = 'VETTING_CAMPAIGN_SYNC',
}

export enum WorkerApplicationItemCompletionStatus {
  ELIGIBLE = 'ELIGIBLE',
  INELIGIBLE = 'INELIGIBLE',
  OPS_WAIVED = 'OPS_WAIVED',
  OPS_MARKED_INELIGIBLE = 'OPS_MARKED_INELIGIBLE',
  OPS_MARKED_ELIGIBLE = 'OPS_MARKED_ELIGIBLE',
  INCONCLUSIVE = 'INCONCLUSIVE',
  EXPIRED = 'EXPIRED',
  BIZ_WAIVED = 'BIZ_WAIVED',
}

export enum WorkerApplicationExclusionType {
  INVITED_TO_SHIFT = 'INVITED_TO_SHIFT',
  IN_PROGRESS = 'IN_PROGRESS',
  OPS_QUALIFIED = 'OPS_QUALIFIED',
  COMPLETED = 'COMPLETED',
  EXCLUDE_ALL_WORKERS = 'EXCLUDE_ALL_WORKERS',
}

export const ELIGIBLE_COMPLETION_STATUSES = [
  WorkerApplicationItemCompletionStatus.ELIGIBLE,
  WorkerApplicationItemCompletionStatus.OPS_WAIVED,
  WorkerApplicationItemCompletionStatus.OPS_MARKED_ELIGIBLE,
  WorkerApplicationItemCompletionStatus.BIZ_WAIVED,
]

export type WorkerApplicationItem = {
  id: string
  workerApplicationId: string
  sourceId?: string
  status: WorkerApplicationItemStatus
  statusHistory?: WorkerApplicationItemStatusHistory[]
  completionStatus?: WorkerApplicationItemCompletionStatus
  originalCompletionStatus?: WorkerApplicationItemCompletionStatus
  applicationItem: ApplicationItem
  createdAt: Date
  updatedAt: Date
}

export type WorkerApplicationSection = {
  sectionIndex: ApplicationSectionIndex
  workerApplicationItems: WorkerApplicationItem[]
}

export enum WorkerApplicationQualificationStatus {
  OPS_QUALIFIED = 'OPS_QUALIFIED',
  OPS_DISQUALIFIED = 'OPS_DISQUALIFIED',
  OPS_MAYBE = 'OPS_MAYBE',
  OPS_FOLLOW_UP = 'OPS_FOLLOW_UP',
  OPS_CALL_AGAIN = 'OPS_CALL_AGAIN',
}

export enum OpCallVettingSuggestionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DISCARDED = 'DISCARDED',
}

export type OpCallVettingSignal = {
  score: number
  rationale: string
}

export type OpCallVettingSuggestion = {
  sourceWorkerCommunicationId: string
  model: string
  generatedAt: string
  score: number
  summary: string
  signals: {
    consistency: OpCallVettingSignal
    interestInWorking: OpCallVettingSignal
    reliableTransportation: OpCallVettingSignal
    skills: OpCallVettingSignal
  }
  improvements: {
    missedQuestions: string[]
    suggestedFollowUps: string[]
  }
  suggestedQualification: WorkerApplicationQualificationStatus
  decision?: {
    decidedByUserId: string
    decidedAt: string
    notes?: string
  }
}

export enum ApplicationResultStatus {
  ACTIVE = 'ACTIVE',
  STALE = 'STALE',
  WITHDRAWN = 'WITHDRAWN',
  CANCELED = 'CANCELED',
  ARCHIVED = 'ARCHIVED',
}

export enum ApplicationResultStatusReason {
  DEACTIVATED_EMPLOYMENT_TYPE = 'DEACTIVATED_EMPLOYMENT_TYPE',
  NOT_INTERESTED = 'NOT_INTERESTED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  GOT_ANOTHER_JOB = 'GOT_ANOTHER_JOB',
  NOT_QUALIFIED = 'NOT_QUALIFIED',
  OLD_APPLICATION = 'OLD_APPLICATION',
  // Worker-initiated withdraw reasons (mobile app withdraw flow). Kept in the
  // same enum to mirror the Prisma enum in apps/traba-server-node/prisma/schema.prisma.
  WORKER_SCHEDULE_CONFLICT = 'WORKER_SCHEDULE_CONFLICT',
  WORKER_PAY_CONCERNS = 'WORKER_PAY_CONCERNS',
  WORKER_LOCATION_TOO_FAR = 'WORKER_LOCATION_TOO_FAR',
  WORKER_PROCESS_TOO_LONG = 'WORKER_PROCESS_TOO_LONG',
  WORKER_PERSONAL_REASONS = 'WORKER_PERSONAL_REASONS',
  WORKER_OTHER = 'WORKER_OTHER',
}

export type WorkerApplication = {
  id: string
  applicationId: string
  workerId: string
  status: WorkerApplicationStatus
  result: ApplicationResultStatus
  workerApplicationSections: WorkerApplicationSection[]
  createdAt: Date
  updatedAt: Date
  applicationEntity: ApplicationEntity
  latitude?: number
  longitude?: number
  acquisitionSource?: string
  qualificationStatus?: WorkerApplicationQualificationStatus
  opsNotes?: string
  tagAssociations?: TagWithAssociations[]
  opCallVettingSuggestion?: OpCallVettingSuggestion
  opCallVettingSuggestionStatus?: OpCallVettingSuggestionStatus

  // TODO(ENG-14681): remove workerApplicationItems once all clients use sections
  /** @deprecated This field has been deprecated; access items through sections and the helpers in application.utils.ts  */
  workerApplicationItems: WorkerApplicationItem[]
}

export type WorkerApplicationWithWorkerInfo = WorkerApplication & {
  workerInfo: Pick<
    BasicOpsWorkerDetails,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'accountApprovalStatus'
    | 'phoneNumber'
    | 'reportedGender'
    | 'opsReportedGender'
    | 'tierLevel'
    | 'completedShifts'
    | 'referredBy'
    | 'referredByWorkerId'
    | 'referredByWorkerName'
    | 'referredByWorkerTierLevel'
  > & {
    reliabilityScore: number
    lastActiveAt?: Date
    lastActiveAtApp?: Date
    lastActiveAtComms?: Date
    presenceStatus?: WorkerPresenceStatus
  }
  scores?: RankedWorkerApplicationScore
  totalRankScore: number
}

export type WorkerProfileInfo = WorkerApplicationWithWorkerInfo['workerInfo']

export enum WorkerPresenceStatus {
  ONLINE = 'ONLINE',
  IDLE = 'IDLE',
  STALE = 'STALE',
}

export type GhostProfileInfo = {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  workerId?: string
}

export type GhostWorkerApplication = WorkerApplication & {
  ghostProfileInfo: GhostProfileInfo
  scores?: RankedWorkerApplicationScore
  totalRankScore: number
}

export type WorkerApplicationWithApplicant =
  | GhostWorkerApplication
  | WorkerApplicationWithWorkerInfo

export const doesWorkerAccountExistForWorkerApplication = (
  workerApplication: WorkerApplicationWithApplicant,
): workerApplication is WorkerApplicationWithWorkerInfo => {
  return 'workerInfo' in workerApplication
}

export const isGhostWorkerApplication = (
  workerApplication: WorkerApplicationWithApplicant,
): workerApplication is GhostWorkerApplication => {
  return 'ghostProfileInfo' in workerApplication
}

export const getLinkedWorkerIdFromGhostApplication = (
  workerApplication: WorkerApplicationWithApplicant,
): string | undefined => {
  if (
    isGhostWorkerApplication(workerApplication) &&
    !doesWorkerAccountExistForWorkerApplication(workerApplication)
  ) {
    return workerApplication.ghostProfileInfo.workerId
  }
  return undefined
}

export const getAccountStatusFromWorkerApplication = (
  workerApplication: WorkerApplicationWithApplicant,
): AccountApprovalStatus | undefined => {
  if (doesWorkerAccountExistForWorkerApplication(workerApplication)) {
    return workerApplication.workerInfo.accountApprovalStatus
  }
  return undefined
}

export function getFullNameFromWorkerApplication(
  workerApplication: WorkerApplicationWithApplicant,
): string {
  if (doesWorkerAccountExistForWorkerApplication(workerApplication)) {
    return `${workerApplication.workerInfo.firstName} ${workerApplication.workerInfo.lastName}`.trim()
  }
  return `${workerApplication.ghostProfileInfo.firstName} ${workerApplication.ghostProfileInfo.lastName}`.trim()
}

export type ScoreDetailWithValue = {
  score: number
  value: string | Date
  weight: number
}

export type RankedWorkerApplicationScore = {
  [key in ScoreCriterionKey]?: ScoreDetailWithValue
}

export enum ScoreCriterionKey {
  APPLICATION_STATUS_FRACTION = 'scoreApplicationStatusFraction',
  ATTRIBUTE_MATCH = 'scoreAttributeMatch',
  RESUME_FULL_TIME = 'scoreResumeFullTime',
  RESUME_TENURE = 'scoreResumeTenure',
  RESUME_INDUSTRY = 'scoreResumeIndustry',
  RESUME_PRIOR_COMPANY = 'scoreResumePriorCompany',
  AVAILABILITY_NOW = 'scoreAvailabilityNow',
  RECENT_ACTIVITY = 'scoreRecentActivity',
  PERFORMANCE_TIER = 'scorePerformanceTier',
  IS_FAVORITE = 'scoreIsFavorite',
  COMPANY_SHIFTS_COMPLETED = 'scoreCompanyShiftsCompleted',
  DISTANCE = 'scoreDistance',
  APPLICATION_COMPLETION = 'scoreApplicationCompletion',
  AI_ATTRIBUTE_MATCH = 'scoreAiAttributeMatch',
  AI_QUALIFIED = 'scoreAiQualified',
  AI_RELIABLE_TRANSPORTATION = 'scoreAiReliableTransportation',
  RESUME_FORKLIFT_OPERATOR_MONTHS_OF_EXPERIENCE = 'scoreResumeForkliftOperatorMonthsOfExperience',
  RESUME_MACHINE_OPERATOR_MONTHS_OF_EXPERIENCE = 'scoreResumeMachineOperatorMonthsOfExperience',
  RESUME_PACKER_MONTHS_OF_EXPERIENCE = 'scoreResumePackerMonthsOfExperience',
  RESUME_LUMPER_MONTHS_OF_EXPERIENCE = 'scoreResumeLumperMonthsOfExperience',
  COMPANY_CANDIDATE_FEEDBACK = 'scoreCompanyCandidateFeedback',
  COMPANY_CANDIDATE_ATTRIBUTE_FEEDBACK = 'scoreCompanyCandidateAttributeFeedback',
  OPS_CANDIDATE_FEEDBACK = 'scoreOpsCandidateFeedback',
  OPS_CANDIDATE_ATTRIBUTE_FEEDBACK = 'scoreOpsCandidateAttributeFeedback',
  EXPERIENCED_FIRST = 'scoreExperiencedFirst',
  SCOUT = 'scoreScout',
  VETTED_CONNECTION = 'scoreVettedConnection',
  REFERRAL = 'scoreReferral',
}

export enum ScoreRawDataKey {
  WORKER_APPLICATION_STATUS = 'workerApplicationStatus',
  COMPLETED_STEPS_COUNT = 'completedStepsCount',
  TOTAL_STEPS_COUNT = 'totalStepsCount',
  ATTRIBUTE_MATCH_COUNT = 'attributeMatchCount',
  TOTAL_ATTRIBUTE_COUNT = 'totalAttributeCount',
  MATCHED_ATTRIBUTES_LIST = 'matchedAttributesList',
  AI_ATTRIBUTE_MATCH_COUNT = 'aiAttributeMatchCount',
  AI_MATCHED_ATTRIBUTES_LIST = 'aiMatchedAttributesList',
  RESUME_FULL_TIME_WORK_HISTORY_SCORE = 'resumeFullTimeWorkHistoryScore',
  RESUME_TENURE_AT_PAST_EMPLOYERS_SCORE = 'resumeTenureAtPastEmployersScore',
  RESUME_LIGHT_INDUSTRY_EXPERIENCE_MONTHS = 'resumeLightIndustryExperienceMonths',
  RESUME_HAS_PRIOR_EMPLOYER_MATCH = 'resumeHasPriorEmployerMatch',
  IS_AVAILABLE_NOW = 'isAvailableNow',
  LAST_ACTIVE_AT = 'lastActiveAt',
  TIER_LEVEL = 'tierLevel',
  IS_FAVORITE = 'isFavorite',
  COMPANY_SHIFTS_COMPLETED_COUNT = 'companyShiftsCompletedCount',
  DISTANCE_IN_MILES = 'distanceInMiles',
  HAS_AI_QUALIFIED = 'hasAiQualified',
  HAS_AI_RELIABLE_TRANSPORTATION = 'hasAiReliableTransportation',
  RESUME_FORKLIFT_OPERATOR_MONTHS_OF_EXPERIENCE = 'resumeForkliftOperatorMonthsOfExperience',
  RESUME_MACHINE_OPERATOR_MONTHS_OF_EXPERIENCE = 'resumeMachineOperatorMonthsOfExperience',
  RESUME_PACKER_MONTHS_OF_EXPERIENCE = 'resumePackerMonthsOfExperience',
  RESUME_LUMPER_MONTHS_OF_EXPERIENCE = 'resumeLumperMonthsOfExperience',
  COMPANY_CANDIDATE_ATTRIBUTE_POSITIVE_COUNT = 'companyCandidateAttributePositiveCount',
  COMPANY_CANDIDATE_ATTRIBUTE_NEGATIVE_COUNT = 'companyCandidateAttributeNegativeCount',
  COMPANY_CANDIDATE_ATTRIBUTE_TOTAL_COUNT = 'companyCandidateAttributeTotalCount',
  HAS_REFERRER = 'hasReferrer',
}

export enum WorkerApplicationItemActionSource {
  INTERNAL_USER = 'INTERNAL_USER',
  WORKER = 'WORKER',
  GHOST_WORKER = 'GHOST_WORKER',
  SYSTEM = 'SYSTEM',
  EXTERNAL_VENDOR = 'EXTERNAL_VENDOR',
  VETTING_CAMPAIGN_SYNC = 'VETTING_CAMPAIGN_SYNC',
  BIZ = 'BIZ',
  OPS = 'OPS',
}

export type WorkerApplicationItemStatusHistory = {
  id: string
  createdAt: Date
  updatedAt: Date
  workerApplicationItemId: string
  toStatus: string
  fromStatus?: string
  toCompletionStatus?: string
  actionSourceId: string
  applicationActionSource: string
}

export const QUALIFICATION_STATUS_TO_TEXT: Record<
  WorkerApplicationQualificationStatus,
  string
> = {
  [WorkerApplicationQualificationStatus.OPS_QUALIFIED]: 'Qualified',
  [WorkerApplicationQualificationStatus.OPS_DISQUALIFIED]: 'Disqualified',
  [WorkerApplicationQualificationStatus.OPS_CALL_AGAIN]: 'Call again',
  [WorkerApplicationQualificationStatus.OPS_MAYBE]: 'In progress',
  [WorkerApplicationQualificationStatus.OPS_FOLLOW_UP]: 'Follow up',
}

export const WORKER_APPLICATIONS_OPS_FEEDBACK_DISQUALIFIED_TAG_NAMESPACE =
  'workerApplicationsOpsFeedback' // For disqualified bucket
export const WORKER_APPLICATIONS_OPS_FEEDBACK_FOLLOW_UP_TAG_NAMESPACE =
  'workerApplicationsOpsFollowUpTags' // For follow up bucket
export const WORKER_APPLICATIONS_OPS_FEEDBACK_PHONE_CALL_TAG_NAMESPACE =
  'workerApplicationsOpsPhoneCallTags' // For phone call bucket
export const WORKER_APPLICATIONS_OPS_FEEDBACK_IN_PROGRESS_TAG_NAMESPACE =
  'workerApplicationsOpsInProgressTags' // For in progress bucket (used to be called "maybe" bucket)

export type WorkerApplicationWithShiftDetails =
  WorkerApplicationWithApplicant & {
    shiftDetails?: {
      roleName?: string
      companyName?: string
      companyId?: string
      startTime?: string
      endTime?: string
      shiftIds?: string[]
      shifts?: { id: string }[]
      location?: {
        coords?: {
          latitude?: number
          longitude?: number
        }
      }
    }
  }

export type BulkUpsertWorkerApplicationFeedbackRequest = {
  workerApplicationIds: string[]
  qualificationStatus?: WorkerApplicationQualificationStatus | null
  feedbackTags?: { namespace: string; tagName: string }[]
  opsNotes?: string | null
}

export type BulkUpsertWorkerApplicationFeedbackResponse = {
  fulfilledCount: number
  rejectedIds: string[]
}

export const QUALIFICATION_STATUS_TO_TAG_NAMESPACE = {
  [WorkerApplicationQualificationStatus.OPS_QUALIFIED]: undefined,
  [WorkerApplicationQualificationStatus.OPS_DISQUALIFIED]:
    WORKER_APPLICATIONS_OPS_FEEDBACK_DISQUALIFIED_TAG_NAMESPACE,
  [WorkerApplicationQualificationStatus.OPS_MAYBE]:
    WORKER_APPLICATIONS_OPS_FEEDBACK_IN_PROGRESS_TAG_NAMESPACE,
  [WorkerApplicationQualificationStatus.OPS_FOLLOW_UP]:
    WORKER_APPLICATIONS_OPS_FEEDBACK_FOLLOW_UP_TAG_NAMESPACE,
  [WorkerApplicationQualificationStatus.OPS_CALL_AGAIN]:
    WORKER_APPLICATIONS_OPS_FEEDBACK_PHONE_CALL_TAG_NAMESPACE,
}
